<template>
    <span :class="['base-avatar', sizeClasses[size], { 'is-square': shape === 'square', 'is-disabled': disabled }]" aria-hidden="true" translate="no">
        <slot>
            <span v-if="initials" class="initials">{{ initials }}</span>
        </slot>
    </span>
</template>

<script lang="ts">
export type BaseAvatarSize = "2xs" | "xs" | "sm" | "md" | "lg" | "xl";
export type BaseAvatarShape = "circle" | "square";

interface BaseAvatarProps {
    size?: BaseAvatarSize;
    shape?: BaseAvatarShape;
    initials?: string;
    /** Muted appearance only; the owning control determines whether interaction is disabled. */
    disabled?: boolean;
}
</script>

<script setup lang="ts">
const { size = "md", shape = "circle", initials, disabled = false } = defineProps<BaseAvatarProps>();

const sizeClasses: Record<BaseAvatarSize, string> = {
    "2xs": "size-2xs",
    xs: "size-xs",
    sm: "size-sm",
    md: "size-md",
    lg: "size-lg",
    xl: "size-xl",
};
</script>

<style scoped>
@reference "../../../css/tailwind.css";

@layer components {
    .base-avatar {
        @apply inline-flex items-center justify-center shrink-0 rounded-full;
        background-color: var(--base-avatar-bg, var(--color-avatar-neutral-bg));
        color: var(--base-avatar-text, var(--color-avatar-neutral-text));
    }

    .size-2xs {
        @apply size-6;
        .initials {
            @apply scaffold-details-bold;
        }
    }

    .size-xs {
        @apply size-7;
        .initials {
            @apply body-small-bold;
        }
    }

    .size-sm {
        @apply size-8;
        .initials {
            @apply body-small-bold;
        }
    }

    .size-md {
        @apply size-11;
        .initials {
            @apply body-bold;
        }
    }

    .size-lg {
        @apply size-14;
        .initials {
            @apply title-body;
        }
    }

    .size-xl {
        @apply size-24;
        .initials {
            @apply display-sans-serif;
        }
    }

    .is-square {
        @apply rounded-lgr;
    }

    .is-disabled {
        @apply bg-surface-sunken text-ink-muted;
    }
}
</style>
